import { MonotonicTimeline } from './monotonicTimeline';
import { LiveAudioTrack, type LiveAudioServerApi } from './liveAudioTrack';
import { displayConstraints, recorderOptions, type RecordingMediaMode } from './recorderQuality';
import { acquireRecorderLease, type ReleaseRecorderLease } from './recorderLease';
import {
  RECORDER_STOP_EVENT_TIMEOUT_MS,
  RECORDER_WRITE_DRAIN_TIMEOUT_MS,
} from './recorderTiming';
import type {
  LocalRecordingSession,
  RecorderServerApi,
  RecorderSnapshot,
  RecorderStore,
  RecordingChunk,
  StopReason,
} from './types';
import {
  MAX_RECORDING_DURATION_MS,
  MAX_RECORDING_PAUSE_MS,
  MAX_RECENT_PAUSES,
  recordingDurationLimitMs,
  recordingStartRefusalCode,
  type RecordingStartRefusalCode,
} from './types';

/** How often a recording recorder tells the server it is still recording (TOADO-900). */
export const ALIVE_INTERVAL_MS = 5 * 60_000;
/** How soon a heartbeat that failed is sent again. */
const ALIVE_RETRY_MS = 30_000;

const CHUNK_TIMESLICE_MS = 5_000;
const MAX_PENDING_CHUNK_WRITES = 3;
export const MAX_RECORDING_SNAPSHOT_PNG_BYTES = 29 * 1024 * 1024;
const MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
] as const;

/**
 * The same probe, for a stream with no picture in it (TOADO-380).
 *
 * `MediaRecorder` refuses a video codec for an audio-only stream, so the list
 * above cannot be reused. Opus in WebM is what Chrome records speech into;
 * the bare container is the fallback for anything that disagrees.
 */
const AUDIO_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
] as const;

type StartInput = {
  ownerId: string;
  companyId: string;
  projectId: string;
  microphoneDeviceId: string;
  /**
   * The screen and the narration, or the narration alone (TOADO-380).
   *
   * Absent means video, which is what every existing caller means. In audio
   * mode no display stream is opened at all, so there is no share picker to
   * answer and nothing on screen to choose: the marked moments still carry
   * their screenshots, because those come from `captureVisibleTab` and never
   * came from this stream.
   */
  mediaMode?: RecordingMediaMode;
  resume?: LocalRecordingSession;
};

type AudioMonitor = {
  context: AudioContext;
  analyser: AnalyserNode;
  frame: number;
};

/** Safe, in-memory startup context. Never contains exception messages or IDs. */
export type RecorderStartFailure = Readonly<{
  stage: 'display' | 'lease' | 'microphone' | 'server' | 'session' | 'storage' | 'encoder';
  status?: number;
  code?: RecordingStartRefusalCode;
}>;

export type RecorderControllerDependencies = {
  store: RecorderStore;
  server: RecorderServerApi;
  /**
   * Where the narration goes while recording (TOADO-901). Absent, there is
   * no live audio and every moment is transcribed after Stop, as before.
   */
  liveAudio?: LiveAudioServerApi;
  createLiveAudioRecorder?: (stream: MediaStream, options: MediaRecorderOptions) => MediaRecorder;
  beforeDiscard?: (recordingId: string) => Promise<void>;
  mediaDevices?: Pick<MediaDevices, 'getDisplayMedia' | 'getUserMedia' | 'enumerateDevices'>;
  createStream?: (tracks: MediaStreamTrack[]) => MediaStream;
  createRecorder?: (stream: MediaStream, mimeType: string, options: MediaRecorderOptions) => MediaRecorder;
  supportsMimeType?: (mimeType: string) => boolean;
  createAudioContext?: () => AudioContext;
  captureFrame?: (track: MediaStreamTrack) => Promise<{ screenshot: Blob; thumbnail: Blob }>;
  performanceNow?: () => number;
  wallNow?: () => Date;
  requestFrame?: (callback: FrameRequestCallback) => number;
  cancelFrame?: (handle: number) => void;
  acquireLease?: (ownerId: string) => Promise<ReleaseRecorderLease | null>;
  stopEventTimeoutMs?: number;
  writeDrainTimeoutMs?: number;
  stopParticipantTimeoutMs?: number;
};

async function canvasBlob(
  bitmap: ImageBitmap,
  width: number,
  height: number,
  type: 'image/png' | 'image/webp',
  quality?: number,
): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Snapshot canvas is unavailable.');
    context.drawImage(bitmap, 0, 0, width, height);
    return canvas.convertToBlob({ type, quality });
  }
  if (typeof document === 'undefined') throw new Error('Snapshot canvas is unavailable.');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Snapshot canvas is unavailable.');
  context.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Snapshot encoding failed.')),
      type,
      quality,
    );
  });
}

export function nextSnapshotDimensions(
  width: number,
  height: number,
  encodedBytes: number,
  maxBytes = MAX_RECORDING_SNAPSHOT_PNG_BYTES,
): { width: number; height: number } {
  const scale = Math.min(0.85, Math.sqrt(maxBytes / encodedBytes) * 0.9);
  return {
    width: Math.max(1, Math.floor(width * scale)),
    height: Math.max(1, Math.floor(height * scale)),
  };
}

async function captureVideoTrack(track: MediaStreamTrack): Promise<{ screenshot: Blob; thumbnail: Blob }> {
  if (typeof ImageCapture === 'undefined') throw new Error('Frame capture is unavailable.');
  const capture = new ImageCapture(track) as ImageCapture & { grabFrame(): Promise<ImageBitmap> };
  const bitmap = await capture.grabFrame();
  try {
    const sourceWidth = bitmap.width;
    const sourceHeight = bitmap.height;
    if (sourceWidth <= 0 || sourceHeight <= 0 || sourceWidth * sourceHeight > 100_000_000) {
      throw new Error('Captured frame dimensions are invalid.');
    }
    const scale = Math.min(
      1,
      5_120 / sourceWidth,
      5_120 / sourceHeight,
      Math.sqrt(25_000_000 / (sourceWidth * sourceHeight)),
    );
    let width = Math.max(1, Math.round(sourceWidth * scale));
    let height = Math.max(1, Math.round(sourceHeight * scale));
    let screenshot = await canvasBlob(bitmap, width, height, 'image/png');
    for (let attempt = 0; screenshot.size > MAX_RECORDING_SNAPSHOT_PNG_BYTES && attempt < 8; attempt += 1) {
      const next = nextSnapshotDimensions(width, height, screenshot.size);
      if (next.width === width && next.height === height) break;
      width = next.width;
      height = next.height;
      screenshot = await canvasBlob(bitmap, width, height, 'image/png');
    }
    if (screenshot.size > MAX_RECORDING_SNAPSHOT_PNG_BYTES) {
      throw Object.assign(new Error('Snapshot could not be encoded within the upload limit.'), {
        code: 'snapshot_too_large',
      });
    }
    const thumbnailWidth = Math.min(640, width);
    const thumbnailHeight = Math.max(1, Math.round(height * (thumbnailWidth / width)));
    const thumbnail = await canvasBlob(bitmap, thumbnailWidth, thumbnailHeight, 'image/webp', 0.8);
    return { screenshot, thumbnail };
  } finally {
    bitmap.close();
  }
}

const INITIAL_SNAPSHOT: RecorderSnapshot = {
  phase: 'idle',
  session: null,
  recoveries: [],
  microphone: { kind: 'idle', level: 0 },
  elapsedMs: 0,
  stopReason: null,
  errorCode: null,
  cleanupPending: false,
};

function recorderErrorCode(error: unknown): string {
  if (error instanceof RecorderLeaseUnavailableError) return 'another_tab_recording';
  if (error instanceof RecordingUnavailableError) return 'recording_unavailable';
  if (error instanceof RecorderUnsupportedError) return 'unsupported_browser';
  const name = error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError') return 'permission_denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'device_unavailable';
  if (name === 'NotReadableError') return 'device_busy';
  if (name === 'AbortError') return 'capture_cancelled';
  return 'capture_failed';
}

class RecorderLeaseUnavailableError extends Error {}
class RecorderStartCancelledError extends Error {}
class RecordingUnavailableError extends Error {}
class RecorderUnsupportedError extends Error {}

function stopTracks(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

/**
 * What a local row from before the mode was stored was actually recording.
 *
 * Between the audio mode shipping and the mode being written onto the local
 * session there was a build that stored audio rows with no `mediaMode`.
 * Resuming one of those took the video branch and put a share picker in front
 * of somebody who had opted out of screen capture - the exact failure the
 * stored mode exists to prevent, for the recordings most likely to need a
 * resume. The container is the evidence: an audio recording was encoded into
 * an audio one, and only an audio one.
 */
function legacyResumeMode(resume: LocalRecordingSession | undefined): RecordingMediaMode | undefined {
  if (!resume || resume.mediaMode) return undefined;
  return resume.mimeType.startsWith('audio/') ? 'audio' : undefined;
}

function isNotFound(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'status' in error
    && (error as { status?: unknown }).status === 404;
}

/**
 * Headless recorder state machine. It intentionally has no React imports so
 * the web page, Document PiP control, and extension side panel can share the
 * same lifecycle implementation.
 */
export class RecorderController {
  private readonly listeners = new Set<() => void>();
  private readonly stopParticipants = new Set<(boundaryMs: number, reason: StopReason) => Promise<void>>();
  private snapshot: RecorderSnapshot = INITIAL_SNAPSHOT;
  private displayStream: MediaStream | null = null;
  private microphoneStream: MediaStream | null = null;
  /** The narration sent while recording (TOADO-901), when there is one. */
  private liveAudio: LiveAudioTrack | null = null;
  private preparedDeviceId: string | null = null;
  private combinedStream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private timeline: MonotonicTimeline | null = null;
  private activePartIndex = 0;
  private chunkStartMs = 0;
  private writeTail: Promise<void> = Promise.resolve();
  private pendingWrites = new Set<Promise<void>>();
  private writeFailure: unknown = null;
  private failureCode: 'storage_full' | 'recorder_failed' | null = null;
  private stopPromise: Promise<void> | null = null;
  private resolveRecorderStopped: (() => void) | null = null;
  private recorderStopped: Promise<void> = Promise.resolve();
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;
  /** When the server was last told this recorder is recording (TOADO-900). */
  private aliveSentAt = Number.NEGATIVE_INFINITY;
  private audioMonitor: AudioMonitor | null = null;
  private lastMeterEmitAt = 0;
  private recoveryOwnerId: string | null = null;
  private releaseLease: ReleaseRecorderLease | null = null;
  private readonly observedMicrophoneTracks = new WeakSet<MediaStreamTrack>();
  private persistenceAbort: AbortController | null = null;
  private displayEndedBeforeStart = false;
  private recorderStopObserved = false;
  private startGeneration = 0;
  private pendingStart: Promise<void> | null = null;
  private signOutPending = false;
  private microphonePreparationGeneration = 0;
  private startFailure: RecorderStartFailure | null = null;
  /** Where the running walkthrough stops: its plan's length (TOADO-496). */
  private durationLimitMs = MAX_RECORDING_DURATION_MS;
  /** Time spent paused in earlier pauses of this recording, on the performance clock. */
  private pausedTotalMs = 0;
  /** When the current pause began, on the performance clock; null while not paused. */
  private pausedSince: number | null = null;
  /** The same pauses on the wall clock, for the snapshot (`RecorderSnapshot.pauses`). */
  private pauses: Array<{ from: number; to?: number }> = [];
  /** From when `pauses` is complete (`RecorderSnapshot.pausesFrom`). */
  private pausesFrom = 0;

  constructor(private readonly dependencies: RecorderControllerDependencies) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  registerStopParticipant(
    participant: (boundaryMs: number, reason: StopReason) => Promise<void>,
  ): () => void {
    this.stopParticipants.add(participant);
    return () => this.stopParticipants.delete(participant);
  }

  getSnapshot = (): RecorderSnapshot => this.snapshot;

  getStartFailure = (): RecorderStartFailure | null => this.startFailure;

  getTimelineMs(): number {
    return Math.min(
      this.timeline?.now() ?? this.snapshot.elapsedMs,
      this.durationLimitMs,
    );
  }

  async captureDisplayFrame(): Promise<{ screenshot: Blob; thumbnail: Blob }> {
    if (!['recording', 'stopping'].includes(this.snapshot.phase)) {
      throw new Error('The recorder is not capturing a display.');
    }
    const track = this.displayStream?.getVideoTracks().find((candidate) => candidate.readyState === 'live');
    if (!track) throw new Error('The captured display is no longer available.');
    return (this.dependencies.captureFrame ?? captureVideoTrack)(track);
  }

  private publish(patch: Partial<RecorderSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  private diagnostic(code: string): void {
    if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
    window.dispatchEvent(new CustomEvent('vd:recorder-diagnostic', {
      detail: { code, phase: this.snapshot.phase },
    }));
  }

  private isCaptureBusy(): boolean {
    return [
      'requesting-display',
      'requesting-microphone',
      'starting',
      'recording',
      'stopping',
    ].includes(this.snapshot.phase);
  }

  private get mediaDevices() {
    const devices = this.dependencies.mediaDevices
      ?? (typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined);
    if (!devices) throw new Error('Media devices are unavailable.');
    return devices;
  }

  private wallNow(): Date {
    return this.dependencies.wallNow?.() ?? new Date();
  }

  private performanceNow(): number {
    return this.dependencies.performanceNow?.() ?? performance.now();
  }

  /**
   * Enumerate audio inputs, obtaining microphone permission first if we do
   * not already have it.
   *
   * `enumerateDevices()` is permission-gated: until the origin has been
   * granted microphone access, Chrome returns a SINGLE audioinput entry with
   * an empty `deviceId` and an empty `label`. Enumerating without that grant
   * therefore produces a picker offering one nameless device that cannot be
   * meaningfully selected, and a `deviceId` that matches nothing — which
   * reads as "the microphone list is broken" rather than "we need
   * permission". Verified in Chromium: no grant returns
   * `[{deviceId: '', label: ''}]`, with a grant the same machine returns
   * every input with real ids and labels.
   *
   * A short-lived `getUserMedia` call is the only way to obtain the grant, so
   * we make one and immediately stop its tracks. The recording page calls
   * this on mount, so the prompt arrives when the user opens the recorder,
   * which is when they expect to be asked.
   *
   * A denied prompt is not an error here: we return whatever enumeration
   * gives us and let the caller render its unavailable state. The recorder
   * cannot start without a tested microphone regardless.
   */
  async listMicrophones(): Promise<MediaDeviceInfo[]> {
    const audioInputs = async () => (await this.mediaDevices.enumerateDevices())
      .filter((device) => device.kind === 'audioinput');

    let devices = await audioInputs();
    const needsPermission = devices.length === 0
      || devices.every((device) => !device.deviceId && !device.label);
    if (!needsPermission) return devices;

    try {
      stopTracks(await this.mediaDevices.getUserMedia({ audio: true, video: false }));
      devices = await audioInputs();
    } catch {
      // Denied, dismissed, or no input hardware. Fall through with whatever
      // enumeration reported so the caller can show its own empty state.
    }
    return devices;
  }

  async prepareMicrophone(deviceId: string): Promise<void> {
    if (this.isCaptureBusy() || this.signOutPending) return;
    this.releaseMicrophone();
    const generation = this.microphonePreparationGeneration;
    this.publish({ microphone: { kind: 'requesting', level: 0 }, errorCode: null });
    try {
      const stream = await this.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
        video: false,
      });
      if (generation !== this.microphonePreparationGeneration || this.isCaptureBusy() || this.signOutPending) {
        stopTracks(stream);
        return;
      }
      const track = stream.getAudioTracks()[0];
      if (!track) {
        stopTracks(stream);
        this.publish({ microphone: { kind: 'unavailable', level: 0 } });
        return;
      }
      this.microphoneStream = stream;
      this.preparedDeviceId = deviceId;
      this.observeMicrophoneTrack(track, stream);
      this.startAudioMonitor(stream, deviceId);
      this.publish({ microphone: { kind: 'ready', level: 0, deviceId } });
    } catch (error) {
      if (generation !== this.microphonePreparationGeneration) return;
      const code = recorderErrorCode(error);
      this.diagnostic(`microphone_${code}`);
      this.publish({
        microphone: code === 'permission_denied'
          ? { kind: 'denied', level: 0 }
          : { kind: 'error', level: 0 },
        errorCode: code,
      });
    }
  }

  private observeMicrophoneTrack(track: MediaStreamTrack, stream: MediaStream): void {
    if (this.observedMicrophoneTracks.has(track)) return;
    this.observedMicrophoneTracks.add(track);
    track.addEventListener('ended', () => {
      if (this.microphoneStream !== stream) return;
      if (this.snapshot.phase === 'recording' || this.snapshot.phase === 'stopping') {
        void this.stop('microphone-ended');
        return;
      }
      this.releaseMicrophone();
      this.publish({ microphone: { kind: 'unavailable', level: 0 } });
    }, { once: true });
  }

  private startAudioMonitor(stream: MediaStream, deviceId: string): void {
    const createContext = this.dependencies.createAudioContext
      ?? (() => new AudioContext());
    try {
      const context = createContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      context.createMediaStreamSource(stream).connect(analyser);
      const buffer = new Uint8Array(analyser.fftSize);
      const requestFrame = this.dependencies.requestFrame ?? requestAnimationFrame;
      const tick = () => {
        if (!this.audioMonitor || this.audioMonitor.analyser !== analyser) return;
        analyser.getByteTimeDomainData(buffer);
        let sumSquares = 0;
        for (const sample of buffer) {
          const normalized = (sample - 128) / 128;
          sumSquares += normalized * normalized;
        }
        const level = Math.min(1, Math.sqrt(sumSquares / buffer.length) * 3.2);
        const now = this.performanceNow();
        if (now - this.lastMeterEmitAt >= 80) {
          this.lastMeterEmitAt = now;
          this.publish({ microphone: { kind: 'ready', level, deviceId } });
        }
        this.audioMonitor.frame = requestFrame(tick);
      };
      const frame = requestFrame(tick);
      this.audioMonitor = { context, analyser, frame };
      void context.resume().catch(() => {});
    } catch {
      // Recording can still proceed if an old browser cannot create the
      // analyser. The UI retains a flat meter and explains that state.
    }
  }

  private releaseMicrophone(): void {
    this.microphonePreparationGeneration++;
    if (this.audioMonitor) {
      const cancelFrame = this.dependencies.cancelFrame ?? cancelAnimationFrame;
      cancelFrame(this.audioMonitor.frame);
      void this.audioMonitor.context.close().catch(() => {});
      this.audioMonitor = null;
    }
    stopTracks(this.microphoneStream);
    this.microphoneStream = null;
    this.preparedDeviceId = null;
  }

  cancelMicrophoneTest(): void {
    if (this.isCaptureBusy()) return;
    this.releaseMicrophone();
    this.publish({ microphone: { kind: 'idle', level: 0 } });
  }

  async loadRecoveries(ownerId: string): Promise<void> {
    if (this.isCaptureBusy()) return;
    this.signOutPending = false;
    this.recoveryOwnerId = ownerId;
    this.publish({ recoveries: [] });
    try {
      let recoveries = await this.dependencies.store.listRecoverable(ownerId, this.wallNow());
      const active = recoveries.filter((session) => session.status === 'active');
      if (active.length > 0) {
        const lease = await (this.dependencies.acquireLease ?? acquireRecorderLease)(ownerId);
        if (lease) {
          try {
            const interrupted = await Promise.all(active.map((session) =>
              this.dependencies.store.markStatus(session.id, 'interrupted', session.elapsedMs)));
            const byId = new Map(interrupted.filter(Boolean).map((session) => [session!.id, session!]));
            recoveries = recoveries
              .filter((session) => session.status !== 'active' || byId.has(session.id))
              .map((session) => byId.get(session.id) ?? session);
          } finally {
            lease();
          }
        } else {
          // Another tab owns the live capture. Do not offer a competing
          // Continue action against its server session or IndexedDB keys.
          recoveries = recoveries.filter((session) => session.status !== 'active');
        }
      }
      if (this.recoveryOwnerId === ownerId) this.publish({ recoveries });
    } catch {
      if (this.recoveryOwnerId === ownerId) {
        this.diagnostic('recovery_unavailable');
        this.publish({ errorCode: 'recovery_unavailable' });
      }
    }
  }

  start(input: StartInput): Promise<void> {
    if (this.signOutPending) return Promise.resolve();
    if (!['idle', 'completed', 'error'].includes(this.snapshot.phase)) return Promise.resolve();
    // An unresolved optional test may not take ownership after capture starts.
    this.microphonePreparationGeneration++;
    this.startFailure = null;
    if (input.resume && input.resume.ownerId !== input.ownerId) {
      this.publish({ phase: 'error', errorCode: 'recovery_owner_mismatch' });
      return Promise.resolve();
    }
    const resumeLimitMs = recordingDurationLimitMs(input.resume?.maxDurationMs);
    if (input.resume && input.resume.elapsedMs >= resumeLimitMs) {
      /**
       * Nothing more may be recorded, so this walkthrough is finished - and is
       * stored as finished (security review, 2026-09-29). Published as complete
       * while its row still said interrupted, a window closed before its upload
       * was prepared left it to come back next time as an interruption to
       * resume, which would stop again at once.
       */
      // As a start in its own right (review round 2): a second start, a reset
      // or a sign-out while the row is written takes the generation, and this
      // then publishes nothing over it; sign-out waits on `pendingStart`.
      const resume = input.resume;
      const generation = ++this.startGeneration;
      const operation = this.dependencies.store.markStatus(resume.id, 'completed', resumeLimitMs)
        .catch(() => null)
        .then((stored) => {
          if (generation !== this.startGeneration) return;
          this.publish({
            phase: 'completed',
            session: stored ?? { ...resume, status: 'completed', elapsedMs: resumeLimitMs },
            elapsedMs: resumeLimitMs,
            stopReason: 'duration-limit',
            errorCode: 'recording_duration_limit',
          });
        });
      this.pendingStart = operation;
      void operation.finally(() => {
        if (this.pendingStart === operation) this.pendingStart = null;
      });
      return operation;
    }
    const generation = ++this.startGeneration;
    const operation = this.startInternal(input, generation);
    this.pendingStart = operation;
    void operation.finally(() => {
      if (this.pendingStart === operation) this.pendingStart = null;
    });
    return operation;
  }

  private assertStartCurrent(generation: number): void {
    if (generation !== this.startGeneration) throw new RecorderStartCancelledError();
  }

  private async startInternal(input: StartInput, generation: number): Promise<void> {

    this.publish({
      phase: 'requesting-display',
      session: input.resume ?? null,
      elapsedMs: input.resume?.elapsedMs ?? 0,
      stopReason: null,
      errorCode: null,
      cleanupPending: false,
    });

    let serverSession: Awaited<ReturnType<RecorderServerApi['create']>> | null = null;
    let localSession: LocalRecordingSession | null = input.resume ?? null;
    let stage: RecorderStartFailure['stage'] = 'display';
    try {
      /**
       * Decided before the first await, and from the resumed session when
       * there is one (TOADO-380).
       *
       * `getDisplayMedia` must be the first awaited call in this user gesture,
       * so a resume cannot ask the server which mode it was and then choose -
       * it has to already know. A recovered audio recording that took the
       * video branch would put a share picker in front of somebody who
       * explicitly opted out of screen capture, which is the one outcome this
       * whole mode exists to prevent.
       */
      /**
       * A resume takes its mode from what it is resuming, and never from the
       * current setting.
       *
       * Falling through to `input.mediaMode` was a defect of its own: the
       * recorder window fills that from storage NOW, so an interrupted video
       * walkthrough whose owner had since switched to narration-only resumed
       * as audio, was refused by the server that correctly called it video,
       * and became unresumable until they guessed to flip the setting back.
       * A row with no mode at all was video, which is what it says it was.
       */
      const requestedMode: RecordingMediaMode = input.resume
        ? (input.resume.mediaMode ?? legacyResumeMode(input.resume) ?? 'video')
        : (input.mediaMode ?? 'video');
      const audioOnly = requestedMode === 'audio';
      const mimeType = this.pickMimeType(audioOnly);
      if (!mimeType) throw new RecorderUnsupportedError('No supported WebM recorder is available.');
      this.displayEndedBeforeStart = false;
      if (audioOnly) {
        /**
         * No picture, so no display stream and no picker (TOADO-380).
         *
         * Skipped rather than opened and discarded: opening one would still
         * put Chrome's "choose what to share" dialog in front of somebody who
         * asked to record their voice, and would hold a capture of their
         * screen open for the length of the walkthrough for nothing.
         */
        this.displayStream?.getTracks().forEach((track) => track.stop());
        this.displayStream = null;
      } else {
        // Must remain the first awaited operation in this user-gesture call.
        // Capped rather than "whatever this display is" (TOADO-379). The
        // extension path does not come through here - it hands over a stream it
        // obtained itself - so it carries the same caps in its own request.
        this.displayStream = await this.mediaDevices.getDisplayMedia(displayConstraints());
        this.assertStartCurrent(generation);
        if (this.displayStream.getVideoTracks().length === 0) {
          throw new DOMException('No display video track was provided.', 'NotFoundError');
        }
        this.displayStream.getVideoTracks().forEach((track) => {
          track.addEventListener('ended', () => {
            this.displayEndedBeforeStart = true;
            void this.stop('display-ended');
          }, { once: true });
        });
      }

      stage = 'lease';
      this.releaseLease = await (this.dependencies.acquireLease ?? acquireRecorderLease)(input.ownerId);
      this.assertStartCurrent(generation);
      if (!this.releaseLease) throw new RecorderLeaseUnavailableError('Another tab is already recording.');

      stage = 'microphone';
      this.publish({ phase: 'requesting-microphone' });
      if (!this.microphoneStream || this.preparedDeviceId !== input.microphoneDeviceId
        || this.microphoneStream.getAudioTracks().every((track) => track.readyState === 'ended')) {
        this.releaseMicrophone();
        this.microphoneStream = await this.mediaDevices.getUserMedia({
          audio: input.microphoneDeviceId ? { deviceId: { exact: input.microphoneDeviceId } } : true,
          video: false,
        });
        this.assertStartCurrent(generation);
        this.preparedDeviceId = input.microphoneDeviceId;
      }
      if (this.microphoneStream.getAudioTracks().length === 0) {
        throw new DOMException('No microphone audio track was provided.', 'NotFoundError');
      }
      this.microphoneStream.getAudioTracks().forEach((track) => this.observeMicrophoneTrack(track, this.microphoneStream!));

      this.publish({ phase: 'starting' });
      stage = 'server';
      try {
        serverSession = input.resume
          ? await this.dependencies.server.get(input.resume.id)
          : await this.dependencies.server.create(input.projectId, requestedMode);
      } catch (error) {
        if (input.resume && isNotFound(error)) throw new RecordingUnavailableError();
        throw error;
      }
      this.assertStartCurrent(generation);
      stage = 'session';
      if (serverSession.companyId !== input.companyId || serverSession.projectId !== input.projectId) {
        throw new Error('Recording session scope did not match the selected project.');
      }
      if (serverSession.status !== 'recording' || serverSession.closedToRecorder) {
        throw new RecordingUnavailableError('Recording session is no longer active.');
      }
      /**
       * The server has to agree about what is being recorded (TOADO-380).
       *
       * An API from before this mode existed ignores the field on create and
       * answers `video`. Recording audio into a session the server believes is
       * video ends the only way it can: a master with no dimensions, probed
       * for them, and a walkthrough reported to its owner as unreadable media
       * - after they have already talked through it. Extensions roll out
       * independently of the API, so that skew is a routine deploy, not a
       * hostile client.
       *
       * An older server omits the field entirely, which is the one case that
       * cannot be told from agreement. Treated as disagreement whenever audio
       * was asked for, because that is the direction that loses work.
       */
      if (audioOnly && serverSession.mediaMode !== 'audio') {
        throw new RecorderUnsupportedError(
          'This Toado server cannot record narration only yet. Record the screen, or update the server.',
        );
      }
      /**
       * And the other direction, which is the one a resume can reach.
       *
       * A session the server calls audio, resumed as video, would open a
       * display stream nobody asked for and then send video into a pipeline
       * that maps only audio - so the picture is captured, discarded, and the
       * person was shown a share picker for nothing. Cheap to check and the
       * failure it prevents is the same class as the one above.
       */
      if (!audioOnly && serverSession.mediaMode === 'audio') {
        throw new RecordingUnavailableError(
          'This walkthrough was recorded as narration only and cannot be resumed with the screen.',
        );
      }
      // Recheck both devices after the network wait, keeping failures specific.
      stage = 'microphone';
      if (!this.microphoneStream || this.microphoneStream.getAudioTracks().every((track) => track.readyState === 'ended')) {
        throw new DOMException('The microphone ended before capture started.', 'NotFoundError');
      }
      // An audio walkthrough has no display to have ended, and asking a null
      // stream whether its tracks are alive is how skipping one would have
      // turned into a crash at the last moment before recording.
      if (!audioOnly) {
        stage = 'display';
        if (this.displayEndedBeforeStart
          || this.displayStream!.getVideoTracks().every((track) => track.readyState === 'ended')) {
          throw new DOMException('Screen sharing ended before capture started.', 'AbortError');
        }
      }

      const currentIso = this.wallNow().toISOString();
      if (input.resume) {
        this.activePartIndex = input.resume.nextPartIndex;
        localSession = {
          ...input.resume,
          status: 'active',
          mimeType,
          nextPartIndex: input.resume.nextPartIndex + 1,
          updatedAt: currentIso,
        };
      } else {
        this.activePartIndex = 0;
        localSession = {
          id: serverSession.id,
          ownerId: input.ownerId,
          companyId: serverSession.companyId,
          projectId: serverSession.projectId,
          status: 'active',
          mediaMode: requestedMode,
          mimeType,
          createdAt: currentIso,
          updatedAt: currentIso,
          expiresAt: serverSession.expiresAt,
          nextPartIndex: 1,
          nextSequence: 0,
          elapsedMs: 0,
          chunkCount: 0,
          storedBytes: 0,
          maxDurationMs: recordingDurationLimitMs(serverSession.limits?.maxDurationMs),
        };
      }
      stage = 'storage';
      await this.dependencies.store.saveSession(localSession);
      this.assertStartCurrent(generation);

      stage = 'encoder';
      const tracks = [
        ...(this.displayStream?.getVideoTracks() ?? []),
        ...this.microphoneStream.getAudioTracks(),
      ];
      this.combinedStream = this.dependencies.createStream?.(tracks) ?? new MediaStream(tracks);
      // Bitrates as well as the mime type: unspecified, Chrome picks its own
      // for a full-resolution screen and a walkthrough costs several times
      // what its content needs (TOADO-379).
      const options = recorderOptions(mimeType);
      this.recorder = this.dependencies.createRecorder?.(this.combinedStream, mimeType, options)
        ?? new MediaRecorder(this.combinedStream, options);
      this.durationLimitMs = recordingDurationLimitMs(localSession.maxDurationMs);
      this.timeline = new MonotonicTimeline(localSession.elapsedMs, () => this.performanceNow());
      this.pausedTotalMs = 0;
      this.pausedSince = null;
      this.pauses = [];
      this.pausesFrom = this.wallNow().getTime();
      this.chunkStartMs = localSession.elapsedMs;
      this.writeFailure = null;
      this.failureCode = null;
      this.persistenceAbort = new AbortController();
      this.writeTail = Promise.resolve();
      this.pendingWrites.clear();
      this.stopPromise = null;
      this.recorderStopped = new Promise((resolve) => { this.resolveRecorderStopped = resolve; });
      this.recorderStopObserved = false;
      this.bindRecorderEvents();
      this.assertStartCurrent(generation);
      this.recorder.start(CHUNK_TIMESLICE_MS);
      this.startLiveAudio(localSession.id, requestedMode, Boolean(input.resume));
      this.publish({ phase: 'recording', session: localSession, elapsedMs: localSession.elapsedMs, paused: false, pauses: [], pausesFrom: this.pausesFrom });
      this.aliveSentAt = Number.NEGATIVE_INFINITY;
      const recordingId = localSession.id;
      this.sayAlive(recordingId);
      this.elapsedTimer = setInterval(() => {
        if (this.snapshot.phase !== 'recording') return;
        this.sayAlive(recordingId);
        if (this.pausedSince !== null) {
          if (this.pausedMs() >= MAX_RECORDING_PAUSE_MS) void this.stop('pause-limit');
          return;
        }
        const elapsedMs = this.getTimelineMs();
        this.publish({ elapsedMs });
        if (elapsedMs >= this.durationLimitMs) void this.stop('duration-limit');
      }, 250);
    } catch (error) {
      const createdId = !input.resume ? serverSession?.id : null;
      this.releaseCaptureMedia();
      if (createdId) {
        await this.dependencies.store.deleteSession(createdId).catch(() => {});
        await this.dependencies.server.discard(createdId).catch(() => {});
      } else if (input.resume && localSession) {
        await this.dependencies.store.markStatus(localSession.id, 'interrupted', localSession.elapsedMs)
          .catch(() => {});
      }
      if (generation === this.startGeneration) {
        const detail = error as { status?: unknown; code?: unknown } | null;
        const status = stage === 'server' ? detail?.status : undefined;
        const refusal = stage === 'server' ? recordingStartRefusalCode(status, detail?.code) : undefined;
        this.startFailure = {
          stage,
          ...(typeof status === 'number' && Number.isInteger(status) && status >= 400 && status <= 599 ? { status } : {}),
          ...(refusal ? { code: refusal } : {}),
        };
        // A refusal the person can act on is its own error code, so the page
        // can say what happened instead of "capture could not start".
        const code = refusal ?? recorderErrorCode(error);
        this.diagnostic(code);
        this.publish({
          phase: 'error',
          session: localSession,
          microphone: { kind: 'idle', level: 0 },
          errorCode: code,
        });
      }
    }
  }

  /**
   * A container this browser will actually record into.
   *
   * The video candidates name a video codec, and `MediaRecorder` refuses one
   * for a stream with no video track - so an audio walkthrough has its own
   * list rather than reusing them (TOADO-380).
   */
  private pickMimeType(audioOnly = false): string {
    const supports = this.dependencies.supportsMimeType
      ?? (typeof MediaRecorder !== 'undefined' ? MediaRecorder.isTypeSupported.bind(MediaRecorder) : null);
    if (!supports) return '';
    const candidates = audioOnly ? AUDIO_MIME_CANDIDATES : MIME_CANDIDATES;
    return candidates.find((candidate) => supports(candidate)) ?? '';
  }

  private bindRecorderEvents(): void {
    const recorder = this.recorder;
    if (!recorder) return;
    recorder.addEventListener('dataavailable', (event: BlobEvent) => {
      if (event.data.size === 0 || !this.timeline || !this.snapshot.session || this.writeFailure) return;
      const pendingLimit = this.snapshot.phase === 'stopping'
        ? MAX_PENDING_CHUNK_WRITES + 1
        : MAX_PENDING_CHUNK_WRITES;
      if (this.pendingWrites.size >= pendingLimit) {
        this.writeFailure = new Error('IndexedDB chunk writes fell behind capture.');
        this.failureCode = 'storage_full';
        this.diagnostic('persistence_backpressure');
        void this.stop('persistence-error');
        return;
      }
      const endMs = this.getTimelineMs();
      const startMs = this.chunkStartMs;
      this.chunkStartMs = endMs;
      if (endMs <= startMs) return;
      const session = this.snapshot.session;
      const sequence = session.nextSequence;
      const nowIso = this.wallNow().toISOString();
      const updatedSession: LocalRecordingSession = {
        ...session,
        nextSequence: sequence + 1,
        elapsedMs: endMs,
        chunkCount: session.chunkCount + 1,
        storedBytes: session.storedBytes + event.data.size,
        updatedAt: nowIso,
      };
      const chunk: RecordingChunk = {
        key: `${session.id}:${String(sequence).padStart(10, '0')}`,
        sessionId: session.id,
        ownerId: session.ownerId,
        partIndex: this.activePartIndex,
        sequence,
        startMs,
        endMs,
        mimeType: event.data.type || session.mimeType,
        size: event.data.size,
        blob: event.data,
        createdAt: nowIso,
      };
      this.publish({ session: updatedSession, elapsedMs: endMs });
      const signal = this.persistenceAbort?.signal;
      const write = this.writeTail.then(() => this.dependencies.store.appendChunk(updatedSession, chunk, signal));
      this.writeTail = write.catch(() => {});
      this.pendingWrites.add(write);
      void write.catch((error) => {
        this.writeFailure = this.writeFailure ?? error;
        this.failureCode = 'storage_full';
        this.diagnostic('persistence_failed');
        void this.stop('persistence-error');
      }).finally(() => this.pendingWrites.delete(write));
    });
    recorder.addEventListener('error', () => {
      this.writeFailure = this.writeFailure ?? new Error('MediaRecorder failed.');
      this.failureCode = 'recorder_failed';
      this.diagnostic('media_recorder_failed');
      this.resolveRecorderStopped?.();
      void this.stop('recorder-error');
    });
    recorder.addEventListener('stop', () => {
      this.recorderStopObserved = true;
      this.resolveRecorderStopped?.();
    }, { once: true });
  }

  /**
   * Pauses a recording in progress: the media and the timeline stand still
   * together, so nothing after the pause is cut at the wrong place. False when
   * there is nothing to pause (not recording, already paused, stopping).
   */
  pause(): boolean {
    if (this.snapshot.phase !== 'recording' || this.snapshot.paused || this.stopPromise) return false;
    if (!this.recorder || this.recorder.state !== 'recording') return false;
    // The clock first and the media straight after, read once: what is cut
    // from the media later is placed by this clock, so the two stop together.
    this.timeline?.pause();
    try {
      this.recorder.pause();
    } catch {
      this.timeline?.resume();
      return false;
    }
    this.liveAudio?.pause();
    this.pausedSince = this.performanceNow();
    const all = [...this.pauses, { from: this.wallNow().getTime() }];
    // Dropped from the front, and the history then starts where the last
    // dropped pause ended: a moment before that cannot be placed.
    for (const dropped of all.slice(0, Math.max(0, all.length - MAX_RECENT_PAUSES))) {
      this.pausesFrom = Math.max(this.pausesFrom, dropped.to ?? dropped.from);
    }
    this.pauses = all.slice(-MAX_RECENT_PAUSES);
    this.publish({ paused: true, elapsedMs: this.getTimelineMs(), pauses: this.pauses, pausesFrom: this.pausesFrom });
    return true;
  }

  /** Resumes a paused recording where it stopped. False when it is not paused. */
  resume(): boolean {
    if (this.snapshot.phase !== 'recording' || !this.snapshot.paused || this.stopPromise) return false;
    if (!this.recorder || this.recorder.state !== 'paused') return false;
    try {
      this.recorder.resume();
    } catch {
      return false;
    }
    this.liveAudio?.resume();
    this.pausedTotalMs = this.pausedMs();
    this.pausedSince = null;
    this.timeline?.resume();
    const to = this.wallNow().getTime();
    this.pauses = this.pauses.map((pause, index) => (index === this.pauses.length - 1 && pause.to === undefined ? { ...pause, to } : pause));
    this.publish({ paused: false, pauses: this.pauses });
    return true;
  }

  /** Time spent paused so far, the current pause included. */
  private pausedMs(): number {
    const current = this.pausedSince === null ? 0 : Math.max(0, this.performanceNow() - this.pausedSince);
    return this.pausedTotalMs + current;
  }

  stop(reason: StopReason = 'user'): Promise<void> {
    if (this.stopPromise) return this.stopPromise;
    if (!this.recorder || !['recording', 'stopping'].includes(this.snapshot.phase)) {
      return Promise.resolve();
    }
    this.stopPromise = this.stopInternal(reason);
    return this.stopPromise;
  }

  private async stopInternal(reason: StopReason): Promise<void> {
    // A paused recording stops where it was paused: the timeline stays still,
    // and MediaRecorder.stop() is valid from the paused state.
    this.pausedSince = null;
    // The narration's last chunk is taken before the microphone is let go,
    // and sent on its own time: Stop never waits for live audio.
    void this.liveAudio?.finish();
    this.publish({ phase: 'stopping', stopReason: reason, paused: false });
    if (this.elapsedTimer) clearInterval(this.elapsedTimer);
    this.elapsedTimer = null;
    try {
      if (this.recorder?.state !== 'inactive') this.recorder?.stop();
    } catch {
      this.writeFailure = this.writeFailure ?? new Error('MediaRecorder stop failed.');
      this.failureCode = 'recorder_failed';
      this.diagnostic('media_recorder_stop_failed');
      this.resolveRecorderStopped?.();
    }
    await new Promise<void>((resolve) => {
      const timer = setTimeout(
        resolve,
        this.dependencies.stopEventTimeoutMs ?? RECORDER_STOP_EVENT_TIMEOUT_MS,
      );
      void this.recorderStopped.finally(() => {
        clearTimeout(timer);
        resolve();
      });
    });
    if (!this.recorderStopObserved && !this.writeFailure) {
      this.writeFailure = new Error('MediaRecorder did not confirm its final stop event.');
      this.failureCode = 'recorder_failed';
      this.diagnostic('media_recorder_stop_timeout');
    }
    const pending = [...this.pendingWrites];
    if (pending.length > 0) {
      let drained = false;
      await new Promise<void>((resolve) => {
        const timer = setTimeout(
          resolve,
          this.dependencies.writeDrainTimeoutMs ?? RECORDER_WRITE_DRAIN_TIMEOUT_MS,
        );
        void Promise.allSettled(pending).then(() => {
          drained = true;
          clearTimeout(timer);
          resolve();
        });
      });
      if (!drained) {
        this.writeFailure = this.writeFailure ?? new Error('IndexedDB writes did not finish in time.');
        this.failureCode = this.failureCode ?? 'storage_full';
        this.persistenceAbort?.abort();
        await Promise.allSettled(pending);
      }
    }

    const elapsedMs = this.getTimelineMs();
    const participants = [...this.stopParticipants];
    let participantTimedOut = false;
    let participantResults: PromiseSettledResult<void>[] = [];
    if (participants.length > 0) {
      const settled = Promise.allSettled(
        participants.map((participant) => participant(elapsedMs, reason)),
      );
      const timeoutMs = this.dependencies.stopParticipantTimeoutMs
        ?? this.dependencies.writeDrainTimeoutMs
        ?? RECORDER_WRITE_DRAIN_TIMEOUT_MS;
      let participantTimer: ReturnType<typeof setTimeout> | null = null;
      participantResults = await Promise.race([
        settled,
        new Promise<PromiseSettledResult<void>[]>((resolve) => {
          participantTimer = setTimeout(() => {
            participantTimedOut = true;
            resolve([]);
          }, timeoutMs);
        }),
      ]);
      if (participantTimer) clearTimeout(participantTimer);
    }
    const settlementPersistenceFailed = participantTimedOut
      || participantResults.some((result) => result.status === 'rejected');
    if (settlementPersistenceFailed) this.diagnostic('stop_participant_failed');
    const session = this.snapshot.session;
    try {
      if (session) {
        if (this.writeFailure) {
          const interrupted = await this.dependencies.store.markStatus(session.id, 'interrupted', elapsedMs)
            .catch(() => null);
          this.publish({
            phase: 'error',
            session: interrupted ?? { ...session, status: 'interrupted', elapsedMs },
            microphone: { kind: 'idle', level: 0 },
            elapsedMs,
            errorCode: this.failureCode ?? (reason === 'recorder-error' ? 'recorder_failed' : 'storage_full'),
            recoveries: [
              interrupted ?? { ...session, status: 'interrupted', elapsedMs },
              ...this.snapshot.recoveries.filter((row) => row.id !== session.id),
            ],
          });
        } else {
          const completed = await this.dependencies.store.markStatus(session.id, 'completed', elapsedMs);
          if (!completed) throw new Error('Local recording session disappeared while stopping.');
          this.publish({
            phase: 'completed',
            session: completed,
            microphone: { kind: 'idle', level: 0 },
            elapsedMs,
            errorCode: settlementPersistenceFailed && reason === 'microphone-ended'
              ? 'microphone_lost_and_settlement_persistence_failed'
              : settlementPersistenceFailed
                ? 'settlement_persistence_failed'
                : reason === 'microphone-ended'
                  ? 'microphone_lost'
                  : reason === 'duration-limit'
                    ? 'recording_duration_limit'
                    : reason === 'pause-limit'
                      ? 'recording_pause_limit'
                      : reason === 'closed-by-server'
                        ? 'recording_closed'
                        : null,
            recoveries: [completed, ...this.snapshot.recoveries.filter((row) => row.id !== completed.id)],
          });
        }
      }
    } catch {
      this.diagnostic('completion_persistence_failed');
      this.publish({
        phase: 'error',
        session: session ? { ...session, status: 'interrupted', elapsedMs } : null,
        microphone: { kind: 'idle', level: 0 },
        elapsedMs,
        errorCode: 'storage_full',
        recoveries: session
          ? [
              { ...session, status: 'interrupted', elapsedMs },
              ...this.snapshot.recoveries.filter((row) => row.id !== session.id),
            ]
          : this.snapshot.recoveries,
      });
    } finally {
      this.releaseCaptureMedia();
    }
  }

  /**
   * Live audio beside a new walkthrough (TOADO-901): a video walkthrough's
   * first part only. An audio walkthrough's master is its audio, and a
   * resumed one is a second part, which the server does not take live audio
   * for; both are transcribed after Stop.
   */
  private startLiveAudio(recordingId: string, mode: RecordingMediaMode, resumed: boolean): void {
    this.closeLiveAudio();
    const server = this.dependencies.liveAudio;
    const tracks = this.microphoneStream?.getAudioTracks() ?? [];
    if (!server || mode !== 'video' || resumed || tracks.length === 0) return;
    const track = new LiveAudioTrack({
      recordingId,
      server,
      stream: this.dependencies.createStream?.(tracks) ?? new MediaStream(tracks),
      timelineMs: () => this.getTimelineMs(),
      mimeType: this.pickMimeType(true),
      createRecorder: this.dependencies.createLiveAudioRecorder,
      // Why live audio stopped, for support: its moments are transcribed after Stop.
      onGiveUp: (reason) => this.diagnostic(`live_audio_${reason}`),
    });
    if (track.start()) this.liveAudio = track;
    else this.diagnostic('live_audio_unavailable');
  }

  /**
   * Tells the server this walkthrough is still being recorded, at most every
   * few minutes, paused or not (TOADO-900, Codex review round 8): the reaper
   * finalises one its recorder has not touched for an hour, and a recording
   * resumed after a crash with no microphone and no new pins would touch
   * nothing else.
   */
  private sayAlive(sessionId: string): void {
    const now = this.performanceNow();
    if (now - this.aliveSentAt < ALIVE_INTERVAL_MS) return;
    this.aliveSentAt = now;
    void this.dependencies.server.alive?.(sessionId).catch((error: unknown) => {
      // Refused for good (Codex review round 11): the server closed this
      // walkthrough while this recorder was away, or it is no longer
      // recording. Stopped and said, rather than recording on into nothing.
      if ((error as { status?: unknown } | null)?.status === 409) {
        if (this.snapshot.phase === 'recording' && this.snapshot.session?.id === sessionId) void this.stop('closed-by-server');
        return;
      }
      // Tried again within the minute (z.ai review round 8), not in five.
      if (this.aliveSentAt === now) this.aliveSentAt = now - ALIVE_INTERVAL_MS + ALIVE_RETRY_MS;
    });
  }

  private closeLiveAudio(): void {
    this.liveAudio?.close();
    this.liveAudio = null;
  }

  private releaseCaptureMedia(): void {
    if (this.elapsedTimer) clearInterval(this.elapsedTimer);
    this.elapsedTimer = null;
    stopTracks(this.displayStream);
    stopTracks(this.combinedStream);
    this.displayStream = null;
    this.combinedStream = null;
    this.recorder = null;
    this.resolveRecorderStopped = null;
    this.timeline = null;
    this.persistenceAbort?.abort();
    this.persistenceAbort = null;
    this.pendingWrites.clear();
    this.writeTail = Promise.resolve();
    this.displayEndedBeforeStart = false;
    this.recorderStopObserved = false;
    this.releaseMicrophone();
    this.releaseLease?.();
    this.releaseLease = null;
  }

  async keepForProcessing(sessionId: string): Promise<void> {
    if (this.isCaptureBusy()) return;
    const updated = await this.dependencies.store.markStatus(sessionId, 'completed').catch(() => {
      this.diagnostic('recovery_update_failed');
      this.publish({ errorCode: 'recovery_unavailable' });
      return null;
    });
    if (!updated) return;
    this.publish({
      recoveries: [updated, ...this.snapshot.recoveries.filter((row) => row.id !== sessionId)],
    });
  }

  async discard(sessionId: string): Promise<void> {
    if (this.isCaptureBusy()) return;
    this.closeLiveAudio();
    try {
      await this.dependencies.beforeDiscard?.(sessionId);
      await this.dependencies.store.deleteSession(sessionId);
    } catch {
      this.diagnostic('local_cleanup_failed');
      this.publish({ errorCode: 'local_cleanup_failed' });
      return;
    }
    let cleanupPending = false;
    try {
      await this.dependencies.server.discard(sessionId);
    } catch (error) {
      cleanupPending = !isNotFound(error);
      if (cleanupPending) this.diagnostic('server_cleanup_deferred');
    }
    const wasCurrent = this.snapshot.session?.id === sessionId;
    this.publish({
      phase: wasCurrent ? 'idle' : this.snapshot.phase,
      session: wasCurrent ? null : this.snapshot.session,
      elapsedMs: wasCurrent ? 0 : this.snapshot.elapsedMs,
      recoveries: this.snapshot.recoveries.filter((row) => row.id !== sessionId),
      cleanupPending,
      errorCode: cleanupPending ? 'server_cleanup_pending' : null,
    });
  }

  reset(): void {
    if (this.isCaptureBusy()) return;
    this.startGeneration += 1;
    this.startFailure = null;
    this.publish({
      phase: 'idle',
      session: null,
      microphone: { kind: 'idle', level: 0 },
      elapsedMs: 0,
      stopReason: null,
      errorCode: null,
      cleanupPending: false,
    });
  }

  clearUserState(): void {
    if (this.snapshot.phase === 'recording' || this.snapshot.phase === 'stopping') return;
    this.closeLiveAudio();
    this.startGeneration += 1;
    this.startFailure = null;
    this.releaseCaptureMedia();
    this.recoveryOwnerId = null;
    this.snapshot = INITIAL_SNAPSHOT;
    this.listeners.forEach((listener) => listener());
  }

  async prepareForSignOut(): Promise<void> {
    this.signOutPending = true;
    const phase = this.snapshot.phase;
    if (phase === 'requesting-display' || phase === 'requesting-microphone' || phase === 'starting') {
      this.startGeneration += 1;
      this.releaseCaptureMedia();
      // Once a server session may exist, wait for start's catch path to
      // discard it before authentication and IndexedDB are cleared.
      if (phase === 'starting') await this.pendingStart?.catch(() => {});
      this.clearUserState();
      return;
    }
    // A start that publishes nothing busy while it works - the at-limit resume,
    // which only writes its row - is still waited for, so no write of it lands
    // after the account's storage is cleared (review round 3).
    if (this.pendingStart) {
      this.startGeneration += 1;
      await this.pendingStart.catch(() => {});
    }
    await this.stop('user');
    this.cancelMicrophoneTest();
  }

  cancelSignOut(): void {
    this.signOutPending = false;
  }
}
